import { useCallback, useMemo, useState } from "react";

import { BTSelectItem } from "types/apiResponse/apiResponse";

import { isNullOrWhitespace } from "utilities/string/string";

import { type BTSelectProps } from "commonComponents/btWrappers/BTSelect/BTSelect";

import "./BTSelectCreatable.less";

export const creatableSelectOption: string = "-9999999999";

interface ICommonCreatableProps {
    onOptionCreate: (field: string, value?: string | number, search?: string) => void;
    maxLength: number;
    createableEntityName?: string;
    canCreateEntity: boolean;
}

type CreatableInput<ExtraDataType> = ICommonCreatableProps &
    Pick<BTSelectProps<unknown, ExtraDataType>, "onChange" | "onSearch" | "treeData">;

type UseSelectCreatableResult<ExtraDataType> = Pick<
    BTSelectProps<unknown, ExtraDataType>,
    "onChange" | "onSearch" | "notFoundContent" | "treeData"
>;

interface ISelectItemLike {
    title?: string;
    children?: readonly ISelectItemLike[];
}

export function useSelectCreatable<ExtraDataType>(
    props: CreatableInput<ExtraDataType>,
): UseSelectCreatableResult<ExtraDataType> {
    const {
        onChange,
        onSearch,
        treeData: originalTreeData,
        onOptionCreate,
        maxLength,
        createableEntityName,
        canCreateEntity,
    } = props;

    const [search, setSearch] = useState<string>();

    const maxExceeded = (search?.length ?? 0) > maxLength;

    const treeData = useMemo(() => {
        if (!originalTreeData || !search || maxExceeded) {
            return originalTreeData;
        }
        const createOption = new BTSelectItem<ExtraDataType>({
            id: creatableSelectOption,
            title: createableEntityName
                ? `Create ${createableEntityName} "${search}"`
                : `Create "${search}"`,
            className: "SelectCreatableOption",
        });
        return [...(originalTreeData as readonly unknown[]), createOption];
    }, [maxExceeded, originalTreeData, search, createableEntityName]);

    const handleChange = useCallback(
        (field: string, value?: string | number, selectedItem?: BTSelectItem<ExtraDataType>) => {
            if (value?.toString() === creatableSelectOption) {
                onOptionCreate(field, value, search);
            } else {
                onChange(field, value, selectedItem);
            }
            setSearch(undefined);
        },
        [onChange, onOptionCreate, search],
    );

    const handleChildSearch = useCallback((node: ISelectItemLike, targetTitle: string): boolean => {
        if (node.title && node.title.toLocaleLowerCase() === targetTitle.toLocaleLowerCase()) {
            return true;
        }

        if (node.children && node.children.length > 0) {
            for (const child of node.children) {
                if (handleChildSearch(child, targetTitle)) {
                    return true;
                }
            }
        }
        return false;
    }, []);

    const handleSearch = useCallback(
        (value: string) => {
            const removeCreate =
                isNullOrWhitespace(value) ||
                (originalTreeData as readonly ISelectItemLike[] | undefined)?.find((item) =>
                    handleChildSearch(item, value),
                );
            setSearch(removeCreate ? undefined : value);
            onSearch?.(value);
        },
        [originalTreeData, handleChildSearch, onSearch],
    );

    if (!canCreateEntity) {
        return { treeData: originalTreeData } as UseSelectCreatableResult<ExtraDataType>;
    }

    const notFoundContent =
        search && maxExceeded ? `${search.length - maxLength} characters over` : undefined;

    return {
        onChange: handleChange,
        onSearch: handleSearch,
        notFoundContent,
        treeData,
    } as UseSelectCreatableResult<ExtraDataType>;
}
